import { api, isConnectionError } from "@/lib/api";

export const failText = (err, what) =>
  !isConnectionError(err) ? `${what}. Bitte versuche es später erneut.`
    : err?.response ? `Das VISIBILIS-System antwortet gerade nicht. ${what} – bitte später erneut versuchen.`
    : `Die Verbindung ist unterbrochen. ${what} – bitte erneut versuchen.`;

// Returns null on success or a German message; the Core decides what is allowed.
export async function requestContact(memberId) {
  try {
    await api.post("/member/conversations/direct", { target_member_id: memberId });
    return null;
  } catch (err) {
    const s = err.response?.status;
    if (!isConnectionError(err) && s === 403) return "Diese Person nimmt derzeit keine Kontaktanfragen an.";
    if (!isConnectionError(err) && s === 429) return "Bitte warte etwas, bevor du erneut eine Anfrage sendest.";
    if (!isConnectionError(err) && s === 409) return "Zu dieser Person gibt es bereits eine Anfrage oder einen Kontakt.";
    return failText(err, "Die Anfrage wurde nicht gesendet");
  }
}

// Re-reads the documented lists; `ok: false` when the confirmation could not be loaded.
async function findIn(path, key, id) {
  try {
    const r = await api.get(path);
    const list = Array.isArray(r.data?.[key]) ? r.data[key] : [];
    return { ok: true, item: list.find((x) => x && String(x.id) === String(id)) || null };
  } catch {
    return { ok: false, item: null };
  }
}

export async function loadGroup(id) {
  const r = await findIn("/member/groups", "groups", id);
  return { ok: r.ok, group: r.item };
}

export async function loadActivity(id) {
  const r = await findIn("/member/activities?category=all", "activities", id);
  return { ok: r.ok, activity: r.item };
}

// v2.10 offer acceptance: POST without a body; only status and error body are passed on, a fresh GET decides what is shown.
export async function acceptOffer(path) {
  try {
    const r = await api.post(path);
    return { status: r.status, data: r.data };
  } catch (err) {
    if (err.memberGuard) return { guard: true };
    if (isConnectionError(err)) return { status: null, data: null };
    return { status: err.response?.status ?? null, data: err.response?.data ?? null };
  }
}

export async function runAction(method, path, what, body) {
  try {
    await (body === undefined ? api[method](path) : api[method](path, body));
    return null;
  } catch (err) {
    return failText(err, what);
  }
}

const STATUS_TEXT = { 403: "Dafür fehlt dir die Berechtigung.", 404: "Der Inhalt ist nicht (mehr) verfügbar.", 409: "Der Inhalt wurde inzwischen geändert.", 413: "Der Text ist zu lang.", 422: "Die Eingabe wurde nicht angenommen.", 429: "Zu viele Versuche – bitte warte einen Moment." };

// `rejected` only for a definite refusal (blocked before sending or a known 4xx); anything else is unclear to callers.
export async function mutate(method, path, body, what) {
  try {
    const r = await (body === undefined ? api[method](path) : api[method](path, body));
    return { data: r.data, rejected: null, status: r.status };
  } catch (err) {
    const s = err.response?.status;
    if (err.memberGuard) return { data: null, rejected: `${what}. In dieser Umgebung ist das Senden gesperrt.`, status: null };
    if (!isConnectionError(err) && Object.prototype.hasOwnProperty.call(STATUS_TEXT, s)) return { data: null, rejected: `${what}. ${STATUS_TEXT[s]}`, status: s };
    return { data: null, rejected: null, status: s ?? null };
  }
}
